import React, { useState, useRef } from 'react';
import { Card, Button, Input } from '../components/UI';
import { useLeague } from '../components/LeagueContext';
import { Track } from '../types';
import { Edit2, Plus, Trash2, Upload, X, MapPin, Image as ImageIcon, Flag } from 'lucide-react';
import { apiRequest } from '../utils/api';
import { API_BASE_URL } from '../config';
import { PageHero } from '../components/PageHero';

export const TracksAdmin: React.FC = () => {
    const { tracks, refreshData } = useLeague();
    const [editingTrack, setEditingTrack] = useState<Partial<Track> | null>(null);
    const [isNew, setIsNew] = useState(false);
    const [saving, setSaving] = useState(false);
    
    // Upload Refs
    const flagInputRef = useRef<HTMLInputElement>(null);
    const layoutInputRef = useRef<HTMLInputElement>(null);

    const handleEdit = (track: Track) => {
        setEditingTrack({ ...track });
        setIsNew(false);
    };

    const handleCreate = () => {
        setEditingTrack({ name: '', location: '', flagUrl: '', layoutUrl: '' });
        setIsNew(true);
    };

    const handleSave = async () => {
        if (!editingTrack || !editingTrack.name || !editingTrack.location) return;
        setSaving(true);
        try {
            if (isNew) {
                await apiRequest('tracks.php', 'POST', editingTrack);
            } else {
                await apiRequest('tracks.php', 'PUT', editingTrack);
            }
            await refreshData();
            setEditingTrack(null);
        } catch (e: any) {
            alert("Fehler beim Speichern der Strecke: " + e.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id: string) => {
        if(!confirm("Diese Strecke wirklich löschen? Verknüpfte Rennen können davon betroffen sein.")) return;
        try {
            await apiRequest(`tracks.php?id=${id}`, 'DELETE');
            refreshData();
        } catch(e: any) {
            alert(e.message);
        }
    };

    const handleUpload = (e: React.ChangeEvent<HTMLInputElement>, field: 'flagUrl' | 'layoutUrl') => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setEditingTrack(prev => prev ? ({ ...prev, [field]: reader.result as string }) : null);
            };
            reader.readAsDataURL(file);
        }
    };

    // Helper Image URL
    const getUrl = (url?: string) => {
        if (!url) return undefined;
        if (url.startsWith('http') || url.startsWith('data:')) return url;
        const baseUrl = API_BASE_URL === '.' ? '' : API_BASE_URL;
        if (baseUrl && !url.startsWith('/')) return `${baseUrl}/${url}`;
        if (baseUrl) return `${baseUrl}${url}`;
        return url;
    };

    return (
        <div className="mx-auto max-w-7xl space-y-6 animate-fade-in pb-20">
            <PageHero
                eyebrow="Circuit Library"
                title="Streckendatenbank"
                description="Strecken, Länderbilder und Layouts als gemeinsame Grundlage für Kalender, Rennberichte und Grafiken verwalten."
                icon={MapPin}
                accent="#3b82f6"
                stats={[{ label: 'Strecken', value: tracks.length }, { label: 'Mit Flagge', value: tracks.filter(track => track.flagUrl).length }, { label: 'Mit Layout', value: tracks.filter(track => track.layoutUrl).length }]}
                actions={<Button icon={Plus} onClick={handleCreate}>Strecke anlegen</Button>}
            />

            {/* Edit Modal */}
            {editingTrack && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <Card className="w-full max-w-2xl bg-f1-dark border-gray-700 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6 border-b border-gray-800 pb-2">
                    <h2 className="text-xl font-bold uppercase">{isNew ? 'Strecke anlegen' : 'Strecke bearbeiten'}</h2>
                            <button onClick={() => setEditingTrack(null)} className="text-gray-400 hover:text-white"><X /></button>
                        </div>
                        
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Input 
                                    label="Streckenname" 
                                    placeholder="z. B. Silverstone Circuit"
                                    value={editingTrack.name} 
                                    onChange={e => setEditingTrack({...editingTrack, name: e.target.value})}
                                />
                                <Input 
                                    label="Ort / Land" 
                                    placeholder="z. B. Großbritannien"
                                    value={editingTrack.location} 
                                    onChange={e => setEditingTrack({...editingTrack, location: e.target.value})}
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                                {/* Flag / Scenic Image */}
                                <div>
                                    <label className="block text-gray-400 text-sm font-bold mb-2 uppercase flex items-center gap-2">
                                        <ImageIcon size={14}/> Scenic/Flag Image
                                    </label>
                                    <div className="relative w-full h-32 bg-gray-900 border border-gray-700 rounded overflow-hidden group">
                                        {editingTrack.flagUrl ? (
                                            <img src={getUrl(editingTrack.flagUrl)} className="w-full h-full object-cover opacity-80" alt="Scenic"/>
                                        ) : (
                                            <div className="flex items-center justify-center h-full text-gray-600 text-xs uppercase">No Image</div>
                                        )}
                                        <div 
                                            className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center cursor-pointer transition-opacity"
                                            onClick={() => flagInputRef.current?.click()}
                                        >
                                            <Upload className="text-white"/>
                                        </div>
                                    </div>
                                    <input type="file" ref={flagInputRef} className="hidden" accept="image/*" onChange={e => handleUpload(e, 'flagUrl')} />
                                    <p className="text-[10px] text-gray-500 mt-1">Wird als Hintergrund im Dashboard und Kalender verwendet.</p>
                                </div>

                                {/* Track Layout */}
                                <div>
                                    <label className="block text-gray-400 text-sm font-bold mb-2 uppercase flex items-center gap-2">
                                        <MapPin size={14}/> Streckenlayout
                                    </label>
                                    <div className="relative w-full h-32 bg-gray-900 border border-gray-700 rounded overflow-hidden group flex items-center justify-center">
                                        {editingTrack.layoutUrl ? (
                                            <img src={getUrl(editingTrack.layoutUrl)} className="max-w-full max-h-full object-contain p-2" alt="Vorschau des Streckenlayouts"/>
                                        ) : (
                                            <div className="text-gray-600 text-xs uppercase">Kein Layout</div>
                                        )}
                                        <div 
                                            className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center cursor-pointer transition-opacity"
                                            onClick={() => layoutInputRef.current?.click()}
                                        >
                                            <Upload className="text-white"/>
                                        </div>
                                    </div>
                                    <input type="file" ref={layoutInputRef} className="hidden" accept="image/*" onChange={e => handleUpload(e, 'layoutUrl')} />
                                    <p className="text-[10px] text-gray-500 mt-1">Empfohlen: transparente PNG-Datei.</p>
                                </div>
                            </div>
                        </div>

                        <div className="mt-8 pt-4 border-t border-gray-800 flex justify-end gap-3">
                            <Button variant="ghost" onClick={() => setEditingTrack(null)}>Abbrechen</Button>
                            <Button onClick={handleSave} disabled={saving}>{saving ? 'Speichert …' : 'Strecke speichern'}</Button>
                        </div>
                    </Card>
                </div>
            )}

            {/* Track Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {tracks.map(track => (
                    <Card key={track.id} className="relative group overflow-hidden p-0 border-0 h-48 flex flex-col justify-end">
                        {/* Background Image */}
                        <div className="absolute inset-0 bg-gray-800 z-0">
                            {track.flagUrl && (
                                <img src={getUrl(track.flagUrl)} className="w-full h-full object-cover opacity-60 transition-transform duration-700 group-hover:scale-110" alt={track.location}/>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                        </div>

                        {/* Layout Overlay (Top Right) */}
                        {track.layoutUrl && (
                            <div className="absolute top-2 right-2 w-16 h-16 opacity-80 z-10">
                                <img src={getUrl(track.layoutUrl)} className="w-full h-full object-contain filter drop-shadow-lg" alt={`Streckenlayout ${track.location}`} onError={event => { if (event.currentTarget.parentElement) event.currentTarget.parentElement.style.display = 'none'; }}/>
                            </div>
                        )}

                        {/* Edit Buttons */}
                        <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity z-20 flex gap-2">
                            <button aria-label={`${track.location} bearbeiten`} title="Bearbeiten" onClick={() => handleEdit(track)} className="p-1.5 bg-gray-700/80 rounded hover:bg-f1-red text-white"><Edit2 size={14}/></button>
                            <button aria-label={`${track.location} löschen`} title="Löschen" onClick={() => handleDelete(track.id)} className="p-1.5 bg-gray-700/80 rounded hover:bg-red-600 text-white"><Trash2 size={14}/></button>
                        </div>

                        {/* Content */}
                        <div className="relative z-10 p-4">
                            <h3 className="text-xl font-bold uppercase italic text-white leading-none">{track.location}</h3>
                            <p className="text-sm text-gray-300 font-semibold mt-1">{track.name}</p>
                        </div>
                    </Card>
                ))}
            </div>
        </div>
    );
};
